import { Link, useParams } from "react-router";
import { ArrowLeft, ArrowRight, Twitter, Linkedin, Link2, Facebook, Instagram } from "lucide-react";
import { useState } from "react";
import { BLOG_POSTS } from "../../data/blogData";

export function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const currentIndex = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prev = BLOG_POSTS[currentIndex - 1];
  const next = BLOG_POSTS[currentIndex + 1];

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F6F2EB] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post not found</h1>
          <Link to="/blog" className="text-muted-foreground hover:text-[#131312] underline">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  const paragraphs = post.content.split("\n\n");
  const [copied, setCopied] = useState(false);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  function handleCopy() {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-screen bg-[#F6F2EB]">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-16">

          {/* Left sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#131312] transition-colors mb-10"
            >
              <ArrowLeft className="size-4" />
              Back to Blog
            </Link>

            <div className="hidden lg:block">
              <div className="border-t border-border pt-8 mt-2">
                <p className="text-xs font-medium text-[#131312] uppercase tracking-wider mb-5">More Articles</p>
                <div className="flex flex-col gap-6">
                  {BLOG_POSTS.filter((p) => p.id !== post.id).slice(0, 4).map((p) => (
                    <Link key={p.id} to={`/blog/${p.slug}`} className="group flex flex-col gap-2">
                      <div className="aspect-[16/9] w-full overflow-hidden rounded-md">
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <p className="text-sm font-medium text-[#131312] leading-tight group-hover:opacity-60 transition-opacity">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main article */}
          <div className="w-full flex-1 lg:max-w-[calc(100%-20rem)]">

            {/* Published line */}
            <div className="mb-4 flex items-center gap-4 text-sm text-muted-foreground">
              <span>Published {post.date} in <span className="lowercase">{post.category}</span></span>
            </div>

            {/* Title */}
            <h1 className="mb-4 text-4xl font-medium text-[#131312] leading-tight">
              {post.title}
            </h1>

            {/* 2:1 image */}
            <div className="relative aspect-[2/1] w-full overflow-hidden rounded-lg mb-4">
              <img
                src={post.image}
                alt={post.title}
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
                decoding="sync"
              />
            </div>

            {/* Author + Share */}
            <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm font-medium text-muted-foreground">
                Author: <span className="text-[#131312]">{post.author}</span> at Colour Club Printers
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground mr-1">Share</span>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Share on Twitter"
                >
                  <Twitter className="size-4" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="size-4" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Share on Facebook"
                >
                  <Facebook className="size-4" />
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(post.title + " " + pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Share on WhatsApp"
                >
                  <svg className="size-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a
                  href={`https://www.instagram.com/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Share on Instagram"
                >
                  <Instagram className="size-4" />
                </a>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center size-9 rounded-lg border border-border bg-white text-[#131312] hover:bg-[#131312] hover:text-white transition-colors"
                  aria-label="Copy link"
                >
                  <Link2 className="size-4" />
                </button>
                {copied && <span className="text-xs text-muted-foreground">Copied!</span>}
              </div>
            </div>

            <div className="mt-10 border-t border-border" />

            {/* Article content */}
            <div className="pt-10 space-y-0">
              {paragraphs.map((para, i) => {
                if (para.startsWith("**") && para.endsWith("**")) {
                  return (
                    <h2 key={i} className="text-2xl font-semibold text-[#131312] tracking-tight mt-10 mb-4 leading-snug">
                      {para.replace(/\*\*/g, "")}
                    </h2>
                  );
                }
                if (para.includes("**")) {
                  const parts = para.split(/\*\*(.*?)\*\*/g);
                  return (
                    <p key={i} className="text-base leading-[1.85] text-[#131312]/70 mb-6">
                      {parts.map((part, j) =>
                        j % 2 === 1
                          ? <strong key={j} className="font-semibold text-[#131312]">{part}</strong>
                          : part
                      )}
                    </p>
                  );
                }
                return (
                  <p key={i} className="text-base leading-[1.85] text-[#131312]/70 mb-6">
                    {para}
                  </p>
                );
              })}
            </div>

            {/* Prev / Next */}
            <div className="mt-16 border-t border-border pt-10 flex flex-col sm:flex-row justify-between gap-6">
              {prev ? (
                <Link to={`/blog/${prev.slug}`} className="group flex flex-col gap-1 max-w-[240px]">
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><ArrowLeft className="size-3" /> Previous</span>
                  <span className="text-sm font-medium text-[#131312] group-hover:opacity-60 transition-opacity leading-tight">{prev.title}</span>
                </Link>
              ) : <div />}
              {next ? (
                <Link to={`/blog/${next.slug}`} className="group flex flex-col gap-1 max-w-[240px] sm:items-end">
                  <span className="text-xs text-muted-foreground flex items-center gap-1 justify-end">Next <ArrowRight className="size-3" /></span>
                  <span className="text-sm font-medium text-[#131312] group-hover:opacity-60 transition-opacity leading-tight">{next.title}</span>
                </Link>
              ) : <div />}
            </div>

          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
